import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../../ui/dialog';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { useReportDraftVisualNodeContext } from '../ReportDraftVisualNodeContext';
import { VisualPreviewThumb } from './ReportDraftVisualPickerDialog';
import {
  buildAnalyticsVisualPresetItems,
  buildAnalyticsVisualSnapshotByPresetId,
  buildBlankVisualNodeData,
  buildCurrentCoverageMapVisualItem,
  collectTemplateVisualInsertItems,
  getCoverageMapPresetNodeData,
  getTemplateVisualNodeDataByItemId,
} from '../visuals/reportDraftVisualInsertCatalog';

const cloneSerializable = (value) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const trimOrEmpty = (value) => String(value || '').trim();

const inferSourceMode = (nodeData, visualType) => {
  const sourceKind = nodeData?.source?.kind;
  const sourceId = nodeData?.source?.id;
  if (visualType === 'chart') {
    if (sourceKind === 'analyticsPreset') return 'analytics';
    if (sourceKind === 'templateBlock') return 'template';
    return 'blank';
  }
  if (visualType === 'map') {
    if (sourceKind === 'templateBlock') return 'template';
    if (sourceId === 'coverage:current-farm-map') return 'coverage';
    return 'blank';
  }
  return 'blank';
};

const mergeNodeForModify = ({ currentNodeData, nextNodeData, titleOverride, sourceInputs }) => {
  const current = cloneSerializable(currentNodeData) || {};
  const next = cloneSerializable(nextNodeData) || {};
  const resolvedTitle = trimOrEmpty(titleOverride);

  if (Object.prototype.hasOwnProperty.call(current, 'caption')) {
    next.caption = current.caption;
  }
  if (Object.prototype.hasOwnProperty.call(current, 'captionLabel')) {
    next.captionLabel = current.captionLabel;
  }
  if (Object.prototype.hasOwnProperty.call(current, 'captionPlaceholder')) {
    next.captionPlaceholder = current.captionPlaceholder;
  }
  if (resolvedTitle) {
    next.title = resolvedTitle;
  }

  if (next.source && typeof next.source === 'object') {
    next.source = {
      ...next.source,
      ...(sourceInputs && typeof sourceInputs === 'object'
        ? {
          inputs: {
            ...(next.source.inputs && typeof next.source.inputs === 'object' ? next.source.inputs : {}),
            ...sourceInputs,
          },
        }
        : {}),
      updatedAt: new Date().toISOString(),
    };
  }

  return next;
};

function PreviewChoiceGrid({ items, selectedId, onSelect }) {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }
  return (
    <div className="report-draft-editor__visual-picker-grid report-draft-editor__visual-modify-grid" role="listbox">
      {items.map((item) => {
        const selected = item.id === selectedId;
        const disabled = Boolean(item.disabled);
        return (
          <button
            key={item.id}
            type="button"
            role="option"
            aria-selected={selected}
            aria-disabled={disabled}
            disabled={disabled}
            className={[
              'report-draft-editor__visual-picker-card',
              selected ? 'is-active' : '',
              disabled ? 'is-disabled' : '',
            ].filter(Boolean).join(' ')}
            onClick={() => {
              if (!disabled) {
                onSelect(item.id);
              }
            }}
          >
            <div className="report-draft-editor__visual-picker-card-thumb">
              <VisualPreviewThumb item={item} />
            </div>
            <div className="report-draft-editor__visual-picker-card-meta">
              <div className="report-draft-editor__visual-picker-card-title-row">
                <span className="report-draft-editor__visual-picker-card-title">{item.label}</span>
              </div>
              {item.description ? (
                <span className="report-draft-editor__visual-picker-item-description">{item.description}</span>
              ) : null}
              {item.disabled && item.disabledReason ? (
                <span className="report-draft-editor__visual-picker-item-disabled-reason">{item.disabledReason}</span>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}

PreviewChoiceGrid.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    preview: PropTypes.object,
    disabled: PropTypes.bool,
    disabledReason: PropTypes.string,
  })),
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

PreviewChoiceGrid.defaultProps = {
  items: [],
  selectedId: '',
};

export function ReportDraftVisualModifyDialog({
  open,
  onOpenChange,
  nodeData,
  visualType,
  onApply,
}) {
  const { t } = useTranslation();
  const { templateDocument } = useReportDraftVisualNodeContext();
  const {
    hourlyData,
    dailyData,
    selectedFarm,
    selectedSections,
    timeRange,
    TIME_RANGES,
  } = useAnalytics();

  const templateItems = useMemo(
    () => collectTemplateVisualInsertItems(templateDocument, t).filter(item => item.visualType === visualType),
    [templateDocument, t, visualType]
  );

  const analyticsItems = useMemo(
    () => (visualType === 'chart'
      ? buildAnalyticsVisualPresetItems({
        hourlyData,
        dailyData,
        selectedFarm,
        selectedSections,
        timeRange,
        TIME_RANGES,
        t,
      }).filter(item => item.visualType === 'chart')
      : []),
    [TIME_RANGES, dailyData, hourlyData, selectedFarm, selectedSections, t, timeRange, visualType]
  );

  const coverageItem = useMemo(
    () => (visualType === 'map' ? buildCurrentCoverageMapVisualItem({ selectedFarm, t }) : null),
    [selectedFarm, t, visualType]
  );

  const sourceInputs = nodeData?.source?.inputs && typeof nodeData.source.inputs === 'object'
    ? nodeData.source.inputs
    : {};

  const [sourceMode, setSourceMode] = useState('blank');
  const [titleOverride, setTitleOverride] = useState('');
  const [selectedTemplateItemId, setSelectedTemplateItemId] = useState('');
  const [selectedAnalyticsPresetId, setSelectedAnalyticsPresetId] = useState('');
  const [timeRangeId, setTimeRangeId] = useState('');
  const [sectionFilterMode, setSectionFilterMode] = useState('pageSelected');
  const [customSectionIds, setCustomSectionIds] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!open) {
      setErrorMessage('');
      return;
    }
    setSourceMode(inferSourceMode(nodeData, visualType));
    setTitleOverride(nodeData?.title || '');
    setSelectedTemplateItemId(
      sourceInputs.templateVisualId
      || (nodeData?.source?.kind === 'templateBlock' ? nodeData?.source?.id : '')
      || templateItems[0]?.id
      || ''
    );
    setSelectedAnalyticsPresetId(
      sourceInputs.presetId
      || (nodeData?.source?.kind === 'analyticsPreset' ? nodeData?.source?.id : '')
      || analyticsItems.find(item => !item.disabled)?.id
      || ''
    );
    setTimeRangeId(sourceInputs.timeRangeId || nodeData?.source?.timeRangeId || timeRange?.id || TIME_RANGES?.[0]?.id || '');
    setSectionFilterMode(sourceInputs.sectionFilterMode === 'custom' ? 'custom' : 'pageSelected');
    setCustomSectionIds(Array.isArray(sourceInputs.sectionIds) ? [...sourceInputs.sectionIds] : [...(selectedSections || [])]);
    setErrorMessage('');
  }, [TIME_RANGES, analyticsItems, nodeData, open, selectedSections, sourceInputs.presetId, sourceInputs.sectionFilterMode, sourceInputs.sectionIds, sourceInputs.templateVisualId, sourceInputs.timeRangeId, templateItems, timeRange?.id, visualType]);

  const templatePickerItems = useMemo(
    () => templateItems.map((item) => ({
      id: item.id,
      label: item.label,
      description: item.description,
      disabled: item.disabled,
      disabledReason: item.disabledReason,
      preview: {
        visualType: item.visualType,
        chart: item.nodeData?.chart || null,
        fallbackCoverage: item.nodeData?.fallbackCoverage || null,
      },
    })),
    [templateItems]
  );

  const analyticsPickerItems = useMemo(
    () => analyticsItems.map((item) => ({
      id: item.id,
      label: item.label,
      description: item.description,
      disabled: item.disabled,
      disabledReason: item.disabledReason,
      preview: {
        visualType: 'chart',
        chart: item.nodeData?.chart || null,
      },
    })),
    [analyticsItems]
  );

  const coveragePickerItems = useMemo(() => {
    if (!coverageItem) return [];
    return [{
      id: coverageItem.id,
      label: coverageItem.label,
      description: coverageItem.description,
      disabled: coverageItem.disabled,
      disabledReason: coverageItem.disabledReason,
      preview: {
        visualType: 'map',
        fallbackCoverage: coverageItem.nodeData?.fallbackCoverage || null,
      },
    }];
  }, [coverageItem]);

  const currentFarmMismatch = Boolean(
    sourceMode === 'analytics'
    && nodeData?.source?.farmId
    && selectedFarm?.id
    && nodeData.source.farmId !== selectedFarm.id
  );

  const sectionChoices = Array.isArray(selectedFarm?.sections) ? selectedFarm.sections : [];

  const toggleSection = (sectionId) => {
    setCustomSectionIds((current) => (
      current.includes(sectionId)
        ? current.filter(id => id !== sectionId)
        : [...current, sectionId]
    ));
  };

  const handleApply = () => {
    if (typeof onApply !== 'function') {
      return;
    }
    setErrorMessage('');

    if (sourceMode === 'analytics') {
      if (visualType !== 'chart') {
        setErrorMessage(t('reports.review.editor.visualModify.errors.unsupported', { defaultValue: 'Unsupported visual source configuration.' }));
        return;
      }
      if (currentFarmMismatch) {
        setErrorMessage(t('reports.review.editor.visualModify.errors.farmMismatch', { defaultValue: 'This visual was created from a different farm. Switch the analytics farm to modify it.' }));
        return;
      }
      const result = buildAnalyticsVisualSnapshotByPresetId({
        hourlyData,
        dailyData,
        selectedFarm,
        selectedSections,
        timeRange,
        TIME_RANGES,
        t,
      }, selectedAnalyticsPresetId, {
        timeRangeId,
        sectionFilterMode,
        sectionIds: customSectionIds,
      });
      if (!result?.ok) {
        const reasonKey = result?.reason === 'insufficient_time_range_data'
          ? 'reports.review.editor.visualModify.errors.insufficientTimeRangeData'
          : 'reports.review.editor.visualModify.errors.noData';
        setErrorMessage(t(reasonKey, { defaultValue: 'No data for selected time range/sections.' }));
        return;
      }
      const nextNodeData = mergeNodeForModify({
        currentNodeData: nodeData,
        nextNodeData: result.nodeData,
        titleOverride,
        sourceInputs: {
          kind: 'analyticsPreset',
          presetId: selectedAnalyticsPresetId,
          timeRangeId,
          sectionFilterMode,
          sectionIds: sectionFilterMode === 'custom' ? customSectionIds : selectedSections,
          farmId: selectedFarm?.id,
        },
      });
      onApply(nextNodeData);
      onOpenChange(false);
      return;
    }

    if (sourceMode === 'template') {
      const templateNodeData = getTemplateVisualNodeDataByItemId(templateDocument, selectedTemplateItemId, t);
      if (!templateNodeData) {
        setErrorMessage(t('reports.review.editor.visualModify.errors.templateNotFound', { defaultValue: 'Selected template visual is not available.' }));
        return;
      }
      const nextNodeData = mergeNodeForModify({
        currentNodeData: nodeData,
        nextNodeData: templateNodeData,
        titleOverride,
        sourceInputs: {
          kind: 'templateBlock',
          templateVisualId: selectedTemplateItemId,
        },
      });
      onApply(nextNodeData);
      onOpenChange(false);
      return;
    }

    if (sourceMode === 'coverage') {
      const coverageNodeData = getCoverageMapPresetNodeData({ selectedFarm, t });
      if (!coverageNodeData) {
        setErrorMessage(t('reports.review.editor.visualModify.errors.noData', { defaultValue: 'No data for selected time range/sections.' }));
        return;
      }
      const nextNodeData = mergeNodeForModify({
        currentNodeData: nodeData,
        nextNodeData: coverageNodeData,
        titleOverride,
        sourceInputs: {
          kind: 'systemPreset',
          farmId: selectedFarm?.id,
        },
      });
      onApply(nextNodeData);
      onOpenChange(false);
      return;
    }

    const blankNodeData = buildBlankVisualNodeData({ visualType, t });
    const nextNodeData = mergeNodeForModify({
      currentNodeData: nodeData,
      nextNodeData: blankNodeData,
      titleOverride,
      sourceInputs: {
        kind: 'systemPreset',
      },
    });
    onApply(nextNodeData);
    onOpenChange(false);
  };

  const showAnalytics = visualType === 'chart';
  const showCoverage = visualType === 'map';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="report-draft-editor__visual-modify-dialog" aria-describedby={undefined}>
        <DialogTitle>{t('reports.review.editor.visualModify.title', { defaultValue: 'Modify visual' })}</DialogTitle>
        <DialogDescription>
          {t('reports.review.editor.visualModify.description', { defaultValue: 'Update the visual source and snapshot settings for this block.' })}
        </DialogDescription>

        <div className="report-draft-editor__visual-modify-section">
          <label className="report-draft-editor__visual-modify-label" htmlFor="visual-modify-source-mode">
            {t('reports.review.editor.visualModify.sourceMode', { defaultValue: 'Source' })}
          </label>
          <select
            id="visual-modify-source-mode"
            className="report-draft-editor__visual-modify-select"
            value={sourceMode}
            onChange={(event) => setSourceMode(event.target.value)}
          >
            <option value="blank">{t('reports.review.editor.visualModify.sourceModes.blank', { defaultValue: 'Blank template' })}</option>
            <option value="template">{t('reports.review.editor.visualModify.sourceModes.template', { defaultValue: 'Module/template visual' })}</option>
            {showAnalytics ? (
              <option value="analytics">{t('reports.review.editor.visualModify.sourceModes.analytics', { defaultValue: 'Analytics snapshot' })}</option>
            ) : null}
            {showCoverage ? (
              <option value="coverage">{t('reports.review.editor.visualModify.sourceModes.coverage', { defaultValue: 'Current farm coverage map' })}</option>
            ) : null}
          </select>
        </div>

        <div className="report-draft-editor__visual-modify-section">
          <label className="report-draft-editor__visual-modify-label" htmlFor="visual-modify-title">
            {t('reports.review.editor.visualModify.titleLabel', { defaultValue: 'Title' })}
          </label>
          <input
            id="visual-modify-title"
            type="text"
            className="report-draft-editor__visual-modify-input"
            value={titleOverride}
            onChange={(event) => setTitleOverride(event.target.value)}
            placeholder={t('reports.review.editor.visualModify.titlePlaceholder', { defaultValue: 'Leave as generated title' })}
          />
        </div>

        {sourceMode === 'template' ? (
          <div className="report-draft-editor__visual-modify-section">
            <div className="report-draft-editor__visual-modify-label">
              {t('reports.review.editor.visualModify.templateVisual', { defaultValue: 'Template visual' })}
            </div>
            <PreviewChoiceGrid
              items={templatePickerItems}
              selectedId={selectedTemplateItemId}
              onSelect={setSelectedTemplateItemId}
            />
          </div>
        ) : null}

        {sourceMode === 'analytics' && showAnalytics ? (
          <>
            <div className="report-draft-editor__visual-modify-section">
              <div className="report-draft-editor__visual-modify-label">
                {t('reports.review.editor.visualModify.analyticsPreset', { defaultValue: 'Analytics preset' })}
              </div>
              <PreviewChoiceGrid
                items={analyticsPickerItems}
                selectedId={selectedAnalyticsPresetId}
                onSelect={setSelectedAnalyticsPresetId}
              />
            </div>

            <div className="report-draft-editor__visual-modify-section">
              <label className="report-draft-editor__visual-modify-label" htmlFor="visual-modify-time-range">
                {t('reports.review.editor.visualModify.timeRange', { defaultValue: 'Time range' })}
              </label>
              <select
                id="visual-modify-time-range"
                className="report-draft-editor__visual-modify-select"
                value={timeRangeId}
                onChange={(event) => setTimeRangeId(event.target.value)}
              >
                {(TIME_RANGES || []).map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
            </div>

            <div className="report-draft-editor__visual-modify-section">
              <label className="report-draft-editor__visual-modify-label" htmlFor="visual-modify-section-mode">
                {t('reports.review.editor.visualModify.sectionFilterMode', { defaultValue: 'Sections' })}
              </label>
              <select
                id="visual-modify-section-mode"
                className="report-draft-editor__visual-modify-select"
                value={sectionFilterMode}
                onChange={(event) => setSectionFilterMode(event.target.value)}
              >
                <option value="pageSelected">{t('reports.review.editor.visualModify.sectionModes.pageSelected', { defaultValue: 'Use page selected sections' })}</option>
                <option value="custom">{t('reports.review.editor.visualModify.sectionModes.custom', { defaultValue: 'Custom sections' })}</option>
              </select>
            </div>

            {sectionFilterMode === 'custom' ? (
              <div className="report-draft-editor__visual-modify-section">
                <div className="report-draft-editor__visual-modify-label">
                  {t('reports.review.editor.visualModify.customSections', { defaultValue: 'Custom sections (current farm)' })}
                </div>
                <div className="report-draft-editor__visual-modify-checkboxes">
                  {sectionChoices.map((section) => (
                    <label key={section.id} className="report-draft-editor__visual-modify-checkbox">
                      <input
                        type="checkbox"
                        checked={customSectionIds.includes(section.id)}
                        onChange={() => toggleSection(section.id)}
                      />
                      <span>{section.name || section.locationDescription || section.id}</span>
                    </label>
                  ))}
                  {sectionChoices.length === 0 ? (
                    <div className="report-draft-editor__visual-modify-help">
                      {t('reports.review.editor.visualModify.noSections', { defaultValue: 'No sections available for the current farm.' })}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {currentFarmMismatch ? (
              <div className="report-draft-editor__visual-modify-warning">
                {t('reports.review.editor.visualModify.errors.farmMismatch', { defaultValue: 'This visual was created from a different farm. Switch the analytics farm to modify it.' })}
              </div>
            ) : null}
          </>
        ) : null}

        {sourceMode === 'coverage' && showCoverage ? (
          <div className="report-draft-editor__visual-modify-section">
            <div className="report-draft-editor__visual-modify-label">
              {t('reports.review.editor.visualModify.coverageSource', { defaultValue: 'Coverage source' })}
            </div>
            <PreviewChoiceGrid
              items={coveragePickerItems}
              selectedId={coveragePickerItems[0]?.id || ''}
              onSelect={() => {}}
            />
          </div>
        ) : null}

        {errorMessage ? (
          <div className="report-draft-editor__visual-modify-error" role="alert">
            {errorMessage}
          </div>
        ) : null}

        <div className="report-draft-editor__visual-modify-actions">
          <button
            type="button"
            className="report-draft-editor__visual-modify-btn report-draft-editor__visual-modify-btn--secondary"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="button"
            className="report-draft-editor__visual-modify-btn report-draft-editor__visual-modify-btn--primary"
            onClick={handleApply}
          >
            {t('common.apply', { defaultValue: 'Apply' })}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

ReportDraftVisualModifyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  nodeData: PropTypes.object,
  visualType: PropTypes.oneOf(['chart', 'map', 'image']),
  onApply: PropTypes.func,
};

ReportDraftVisualModifyDialog.defaultProps = {
  nodeData: null,
  visualType: 'chart',
  onApply: undefined,
};

